import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLoading } from '../../../../components/LoadingProvider';
import api from '../../../../api/axios';
import { showSuccess, showError } from '../../../../utils/toast';
import useAuth from '../../../../hooks/useAuth';

export default function FeesPaymentMethod() {
    const { user, isAuthenticated, userRole } = useAuth({ redirectPath: '/login', allowedRoles: ['doctor'] });
    const navigate = useNavigate();
    const { setIsLoading } = useLoading();
    const errorDivRef = useRef(null);
    const [btnDisabled, setBtnDisabled] = useState(false);
    const [errors, setErrors] = useState({
        consultationFee: '',
        panCardNumber: '',
        upiId: '',
        accountNumber: '',
        ifscCode: '',
        accountHolderName: '',
        bankName: '',
    });
    const [formData, setFormData] = useState({
        consultationFee: '',
        panCardNumber: '',
        upiId: '',
        accountNumber: '',
        ifscCode: '',
        accountHolderName: '',
        bankName: '',
    });

    useEffect(() => {
        document.title = 'Varosa - Doctor Fees Payment Method Edit';
        if (!isAuthenticated || !user) {
            navigate('/login');
            return;
        }
        fetchDoctorData();
    }, []);

    const validateForm = () => {
        let all_good = true;
        let error = {
            consultationFee: '',
            panCardNumber: '',
            upiId: '',
            accountNumber: '',
            ifscCode: '',
            accountHolderName: '',
            bankName: '',
        };
        
        if(formData.consultationFee.trim() === ''){
            error.consultationFee = 'Please enter a consultation fee.';
            all_good = false;
        }
        if(formData.panCardNumber.trim() === ''){
            error.panCardNumber = 'Please enter a valid PAN Card Number.';
            all_good = false;
        }
        // if(formData.upiId.trim() === ''){
        //     error.upiId = 'Please select a valid UPI Id.';
        //     all_good = false;
        // }
        if(formData.accountNumber.trim() === ''){
            error.accountNumber = 'Please enter a valid Account Number.';
            all_good = false;
        }
        if(formData.ifscCode.trim() === ''){
            error.ifscCode = 'Please enter a valid IFSC Code.';
            all_good = false;
        }
        if(formData.accountHolderName.trim() === ''){
            error.accountHolderName = 'Please enter a valid Account Holder Name.';
            all_good = false;
        }
        if(formData.bankName.trim() === ''){
            error.bankName = 'Please enter a valid Bank Name.';
            all_good = false;
        }
        setErrors(error);
        return all_good;
    };

    const fetchDoctorData = async () => {
        setBtnDisabled(true);
        try {
            const response = await api.get('/'+userRole+'/bank-details');
            if (response?.data?.result === 'success') {
                setFormData({
                    consultationFee: response.data.data.consultation_fee || '',
                    panCardNumber: response.data.data.pan_card_number || '',
                    upiId: response.data.data.upi_id || '',
                    accountNumber: response.data.data.account_number || '',
                    ifscCode: response.data.data.ifsc_code || '',
                    accountHolderName: response.data.data.account_holder_name || '',
                    bankName: response.data.data.bank_name || '',
                });
            }else if (response?.data?.result === 'failed') {
                showError(response?.data?.message);
                if(response?.data?.errors){
                    response?.data?.errors.forEach((item, index, arr) => {
                        showError(item);
                    });
                }
            } else {
                showError('Something went wrong! Please try again later.');
            }
        } catch (error) {
            showError('Something went wrong! Please try again later.');
        } finally {
            setBtnDisabled(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        if (name.includes('.')) {
            const [parent, child] = name.split('.');
            setFormData(prev => ({
                ...prev,
                [parent]: {
                    ...prev[parent],
                    [child]: value
                }
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if(!validateForm()){ 
                errorDivRef.current.scrollIntoView({ behavior: 'smooth' });
                return; 
            }
            const postData = new FormData();
            postData.append(`consultation_fee`, formData.consultationFee);
            postData.append(`pan_card_number`, formData.panCardNumber);
            postData.append(`upi_id`, formData.upiId);
            postData.append(`account_number`, formData.accountNumber);
            postData.append(`ifsc_code`, formData.ifscCode);
            postData.append(`account_holder_name`, formData.accountHolderName);
            postData.append(`bank_name`, formData.bankName);
            setBtnDisabled(true);
            setIsLoading(true);
            const response = await api.post('/'+userRole+'/bank-details', postData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            if (response?.data?.result === 'success') {
                showSuccess(response.data.message);
                fetchDoctorData();
            }else if (response?.data?.result === 'failed') {
                showError(response?.data?.message);
                if(response?.data?.errors){
                    response?.data?.errors.forEach((item, index, arr) => {
                        showError(item);
                    });
                }
            } else {
                showError('Something went wrong! Please try again later.');
            }
        } catch (error) {
            showError(error.response?.data?.message || 'Error updating profile');
        } finally {
            setBtnDisabled(false);
            setIsLoading(false);
        }
    };

    return (
        <div className="container py-5 login-container">
            <div className="row g-3">
                <div className="col-9">
                    <h2 className="mb-4">Fees & Payment Method</h2>
                </div>
                <div className="col-3 text-end">
                    <button onClick={() => window.history.go(-1)} className="btn btn-primary">Back</button>
                </div>
            </div>
            <form onSubmit={handleSubmit} className="needs-validation" ref={errorDivRef}>
                <div className="row g-3">
                    <div className="col-md-6">
                        <label className="form-label" htmlFor="consultationFee">Consultation Fee (₹)</label>
                        <input
                            type="number"
                            className="form-control py-3 border-primary"
                            name="consultationFee"
                            id="consultationFee"
                            value={formData.consultationFee}
                            onChange={handleInputChange}
                            required
                            min="0"
                        />
                        {errors.consultationFee && <div className="invalid-feedback d-block">Please enter a valid consultation fee.</div>}
                    </div>
                    <div className="col-md-6">
                        <label className="form-label" htmlFor="panCardNumber">PAN Card</label>
                        <input
                            type="text"
                            className="form-control py-3 border-primary"
                            name="panCardNumber"
                            id="panCardNumber"
                            value={formData.panCardNumber}
                            onChange={handleInputChange}
                            required
                        />
                        {errors.panCardNumber && <div className="invalid-feedback d-block">Please enter a valid PAN card number.</div>}
                    </div>
                    <div className="col-md-6">
                        <label className="form-label" htmlFor="accountNumber">Account Number</label>
                        <input
                            type="text"
                            className="form-control py-3 border-primary"
                            name="accountNumber"
                            id="accountNumber"
                            value={formData.accountNumber}
                            onChange={handleInputChange}
                        />
                        {errors.accountNumber && <div className="invalid-feedback d-block">Please enter a valid account number.</div>}
                    </div>
                    <div className="col-md-6">
                        <label className="form-label" htmlFor="ifscCode">IFSC Code</label>
                        <input
                            type="text"
                            className="form-control py-3 border-primary"
                            name="ifscCode"
                            id="ifscCode"
                            value={formData.ifscCode}
                            onChange={handleInputChange}
                        />
                        {errors.ifscCode && <div className="invalid-feedback d-block">Please enter a valid IFSC code.</div>}
                    </div>
                    <div className="col-md-6">
                        <label className="form-label" htmlFor="accountHolderName">Account Holder Name</label>
                        <input
                            type="text"
                            className="form-control py-3 border-primary"
                            name="accountHolderName"
                            id="accountHolderName"
                            value={formData.accountHolderName}
                            onChange={handleInputChange}
                        />
                        {errors.accountHolderName && <div className="invalid-feedback d-block">Please enter a valid account holder name.</div>}
                    </div>
                    <div className="col-md-6">
                        <label className="form-label" htmlFor="bankName">Bank Name</label>
                        <input
                            type="text"
                            className="form-control py-3 border-primary"
                            name="bankName"
                            id="bankName"
                            value={formData.bankName}
                            onChange={handleInputChange}
                        />
                        {errors.bankName && <div className="invalid-feedback d-block">Please enter a valid bank name.</div>}
                    </div>
                    <div className="col-md-6">
                        <label className="form-label" htmlFor="upiId">UPI ID (optional)</label>
                        <input
                            type="text"
                            className="form-control py-3 border-primary"
                            name="upiId"
                            id="upiId"
                            value={formData.upiId}
                            onChange={handleInputChange}
                        />
                        {errors.upiId && <div className="invalid-feedback d-block">Please enter a valid UPI ID.</div>}
                    </div>
                </div>

                <div className="mt-4">
                    <button type="submit" className="btn btn-primary" disabled={btnDisabled}>Save</button>
                </div>
            </form>
        </div>
    );
}